import {
	type Action,
	type ActionScopeMap,
	getScopesFor,
	type PermissionScope,
	type permissionManifest,
} from "@msuite/citadel";
import { Select, type SelectOption } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { PermissionScopeTag, type PermissionScopeTagValue } from "@msuite/ui/tag";
import type { TranslationKey } from "@msuite/utils/locales";
import type { RolePermissionFormSchema } from "@msuite/utils/schemas";
import { type Path, useFormContext } from "react-hook-form";
import { scopeExplanationKey } from "@/components/citadel";
import { grantKey } from "./grants";

/** The label of each scope, in the fixed order the scope map offers them. */
const SCOPE_LABELS: Record<PermissionScope, TranslationKey> = {
	organization: "roles.scope_organization",
	own: "roles.scope_own",
	ownProjects: "roles.scope_own_projects",
};

/** What one option renders: its reach tag, and — inside the open list only — its explanation. */
interface ScopeOptionItem {
	tag: PermissionScopeTagValue;
	explanation: string;
}

/** Props Interface */
interface ScopeCellProps {
	item: { id: string } & (typeof permissionManifest)[number];
	action: Action;
	/** The role holds this pair under a conditions template the scope map does not know. */
	isCustom: boolean;
	isDisabled: boolean;
}

export function ScopeCell({ item, action, isCustom, isDisabled }: ScopeCellProps) {
	/** Context */
	const { t } = useTranslation();
	const { control } = useFormContext<RolePermissionFormSchema>();

	/** Computed */
	const name = `grants.${grantKey(action, item.subject)}` as Path<RolePermissionFormSchema>;
	const subject = item.subject as keyof typeof ActionScopeMap;
	const scopes = getScopesFor(subject, action);
	const explain = (tag: PermissionScopeTagValue) => t(scopeExplanationKey(subject, tag));

	// `item` carries the scope the tag renders; it doubles as the truthy marker the select needs
	// to render its button through `renderOption` — without it a revoked cell, whose value is
	// the falsy `false`, would read as "Select" instead of the grey no-access tag.
	const options: Array<SelectOption<ScopeOptionItem, false | PermissionScope>> = isCustom
		? [
				{
					value: false,
					label: t("roles.scope_custom"),
					item: { tag: "custom", explanation: explain("custom") },
				},
			]
		: [
				{
					value: false,
					label: t("roles.scope_none"),
					item: { tag: "none", explanation: explain("none") },
				},
				...scopes.map((scope) => ({
					value: scope,
					label: t(SCOPE_LABELS[scope]),
					item: { tag: scope as PermissionScopeTagValue, explanation: explain(scope) },
				})),
			];

	/** Guard */
	// A subject that does not offer the action has no cell at all, rather than a select whose
	// only answer is "no access" — the pair cannot be granted in the first place.
	if (!item.actions.includes(action)) return null;

	/** Render */
	// The invoices status column's cell shape, exactly: an unpadded cell, a px-2 wrapper, and
	// the boxed select drawing its rounded pill inside it, sized by content — no fixed width,
	// which is what used to clip the longest label. The tag inside carries one colour per reach.
	return (
		<div className="px-2 w-full">
			<Select
				control={control}
				name={name}
				label={undefined}
				withBackdrop
				isRequired={false}
				isNullable={false}
				isDisabled={isDisabled || isCustom}
				className="py-1 rounded-sm"
				data-cy={`role-scope-select-${action}-${item.subject}`}
				options={options}
				// Wider than the narrow cell so the explanations read as sentences, not shreds.
				optionsClassName="min-w-72"
				renderOption={(option) => (
					<div className="min-w-0 v-stack gap-1! items-start">
						<PermissionScopeTag scope={option.tag} />
						{/* The explanation renders only inside the open list; the closed button
						    shows nothing but the reach tag. */}
						<p className="hidden group-[.listbox-option]:block opacity-50 text-xs whitespace-normal">
							{option.explanation}
						</p>
					</div>
				)}
			/>
		</div>
	);
}
